import React, { useState, useEffect } from 'react';
import { VendorTaglineControl } from '../components/VendorTaglineField';
import { Button, Form, Spinner, Modal, Container, Row, Col, Card } from 'react-bootstrap';
import { Switch, FormControlLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
    uploadExactVendorProfileMedia,
} from '../utilities/imageUpload';
import { toast } from 'react-toastify';
import VendorProfileMediaDeleteDialog from '../components/VendorProfileMediaDeleteDialog';
import { useVendorMediaCrop } from '../components/VendorMediaCrop';
import { FaInfoCircle, FaCameraRetro, FaSquareFull, FaSpellCheck, FaStar } from 'react-icons/fa';
import { MdAddAPhoto } from 'react-icons/md';
import ArtworkTemplateLink from '../components/ArtworkTemplateLink';
import VendorAddressAutocomplete from '../components/VendorAddressAutocomplete';
import { useGoogleMaps } from '../GoogleMapsContext';
import { useCountry } from '../context/CountryContext.js';
import { getMarket } from '../config/markets.js';

const useStyles = makeStyles(theme => ({
    label: {
        fontWeight: 'normal',
        fontSize: '12px',
        textTransform: 'capitalize'
    }
}));

const EditRetailerWholesalerForm = ({ retailerWholesaler, canEditName = false, onSave, onCancel }) => {
    const classes = useStyles();
    const { fitVendorMedia, mediaCropDialog } = useVendorMediaCrop();
    const [formData, setFormData] = useState({ ...retailerWholesaler });
    const { mapsApiLoaded } = useGoogleMaps();
    const { activeCountry } = useCountry();
    // Places suggestions are limited to the shop's own market. A row without a
    // country (the admin directory does not project it) uses the market being
    // viewed, as the brand forms do.
    const market = getMarket(retailerWholesaler?.country || activeCountry);
    const hasPickupPoint =
        Number.isFinite(Number(formData.latitude)) &&
        Number.isFinite(Number(formData.longitude)) &&
        formData.latitude !== null && formData.latitude !== '' &&
        formData.longitude !== null && formData.longitude !== '';
    const [uploading, setUploading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [bannerPreview, setBannerPreview] = useState(null);
    const [cardPreview, setCardPreview] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedBannerFile, setSelectedBannerFile] = useState(null);
    const [selectedCardFile, setSelectedCardFile] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [isFormatCorrect, setIsFormatCorrect] = useState(false);
    const [isSizeCorrect, setIsSizeCorrect] = useState(false);
    const [isResolutionCorrect, setIsResolutionCorrect] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    // The saved image whose Delete link was clicked; opens the delete dialog.
    const [mediaDeleteRole, setMediaDeleteRole] = useState(null);

    useEffect(() => {
        setFormData({ ...retailerWholesaler });
    }, [retailerWholesaler]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const previewFile = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('There was an error reading your image.'));
        reader.readAsDataURL(file);
    });

    const handleImageChange = async (event) => {
        let file = event.target.files[0];
        if (!file) {
            setUploadError('No file was selected.');
            toast.error('No file was selected.');
            return;
        }

        setIsProcessing(true);
        try {
            // An image that is not the exact size opens the crop dialog (components/VendorMediaCrop.js).
            file = await fitVendorMedia(file, 'retailer_wholesaler', 'logo');
            if (!file) return; // crop cancelled
            const preview = await previewFile(file);
            setSelectedFile(file);
            setImagePreview(preview);
            setBannerPreview(null);
            setCardPreview(null);
            setFormData(prev => ({ ...prev, imageUrl: preview }));
            setIsFormatCorrect(true);
            setIsSizeCorrect(true);
            setIsResolutionCorrect(true);
            setUploadError(null);
            setShowPreviewModal(true);
        } catch (error) {
            setUploadError(error.message);
            setSelectedFile(null);
            toast.error(error.message);
        } finally {
            setIsProcessing(false);
            event.target.value = '';
        }
    };

    const handleBannerChange = async (event) => {
        let file = event.target.files[0];
        if (!file) {
            setUploadError('No file was selected.');
            toast.error('No file was selected.');
            return;
        }

        setIsProcessing(true);
        try {
            // An image that is not the exact size opens the crop dialog (components/VendorMediaCrop.js).
            file = await fitVendorMedia(file, 'retailer_wholesaler', 'shopBanner');
            if (!file) return; // crop cancelled
            const preview = await previewFile(file);
            setSelectedBannerFile(file);
            setBannerPreview(preview);
            setImagePreview(null);
            setCardPreview(null);
            setFormData(prev => ({ ...prev, shopBannerUrl: preview }));
            setIsFormatCorrect(true);
            setIsSizeCorrect(true);
            setIsResolutionCorrect(true);
            setUploadError(null);
            setShowPreviewModal(true);
        } catch (error) {
            setUploadError(error.message);
            setSelectedBannerFile(null);
            toast.error(error.message);
        } finally {
            setIsProcessing(false);
            event.target.value = '';
        }
    };

    const handleCardChange = async (event) => {
        let file = event.target.files[0];
        if (!file) {
            setUploadError('No file was selected.');
            toast.error('No file was selected.');
            return;
        }

        setIsProcessing(true);
        try {
            // An image that is not the exact size opens the crop dialog (components/VendorMediaCrop.js).
            file = await fitVendorMedia(file, 'retailer_wholesaler', 'cardImage');
            if (!file) return; // crop cancelled
            const preview = await previewFile(file);
            setSelectedCardFile(file);
            setCardPreview(preview);
            setImagePreview(null);
            setBannerPreview(null);
            setFormData(prev => ({ ...prev, cardImageUrl: preview }));
            setIsFormatCorrect(true);
            setIsSizeCorrect(true);
            setIsResolutionCorrect(true);
            setUploadError(null);
            setShowPreviewModal(true);
        } catch (error) {
            setUploadError(error.message);
            setSelectedCardFile(null);
            toast.error(error.message);
        } finally {
            setIsProcessing(false);
            event.target.value = '';
        }
    };

    const uploadImageToCloudinary = async (file, mediaRole = "logo") => {
        if (!file) {
            toast.error("No image file selected!");
            return null;
        }

        try {
            const result = await uploadExactVendorProfileMedia(file, {
                vendorType: "retailer_wholesaler",
                vendorId: retailerWholesaler._id || retailerWholesaler.owner,
                mediaRole,
                entityType: "retailerWholesalerProfile",
                entityId: retailerWholesaler._id || "profile",
                source: "dashboard",
            });
            return result.secure_url;
        } catch (error) {
            console.error('Error uploading the image:', error);
            toast.error(`Failed to upload image: ${error.message}`);
            return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        let updatedData = { ...formData };
        if (selectedFile) {
            setUploading(true);
            const secureUrl = await uploadImageToCloudinary(selectedFile);
            if (!secureUrl) { setUploading(false); return; }
            updatedData = { ...updatedData, imageUrl: secureUrl };
            setUploading(false);
        }

        if (selectedBannerFile) {
            setUploading(true);
            const secureUrl = await uploadImageToCloudinary(selectedBannerFile, "shopBanner");
            if (!secureUrl) { setUploading(false); return; }
            updatedData = { ...updatedData, shopBannerUrl: secureUrl, bannerUrl: "" };
            setUploading(false);
        }

        if (selectedCardFile) {
            setUploading(true);
            const secureUrl = await uploadImageToCloudinary(selectedCardFile, "cardImage");
            if (!secureUrl) { setUploading(false); return; }
            updatedData = { ...updatedData, cardImageUrl: secureUrl };
            setUploading(false);
        }

        onSave(updatedData);
    };

    // An unsaved, freshly-selected file is only a local preview: discard it
    // immediately with no server call and no confirmation. An already-saved
    // media asset opens VendorProfileMediaDeleteDialog, the one delete
    // confirmation shared by every vendor form, which shows every saved image
    // and lets the operator tick more than one.
    const requestRemoveMedia = (mediaRole, field, clearSelectedFile) => {
        if (clearSelectedFile) {
            clearSelectedFile(null);
            setFormData(prev => ({ ...prev, [field]: '' }));
            return;
        }
        setMediaDeleteRole(mediaRole);
    };

    return (
        <Container fluid>
            <Row className="justify-content-md-center">
                <Col md={10}>
                    <Card className="mt-3">
                        <Card.Body>
                            <Card.Title style={{ fontSize: '1.5rem', fontWeight: 500, marginBottom: '20px' }} as="h2">
                                Edit Retailer/Wholesaler
                            </Card.Title>
                            <hr />
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-1 mt-2">
                                    <Form.Label>Profile Image</Form.Label>
                                    <Row>
                                        <Col md={6}>
                                            <Card style={{ width: '100%' }}>
                                                <Card.Body className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '220px' }}>
                                                    {isProcessing && selectedFile ? (
                                                        <Spinner animation="border" />
                                                    ) : formData.imageUrl ? (
                                                        <>
                                                            <img src={formData.imageUrl} alt="Profile" style={{ maxHeight: '220px', maxWidth: '100%' }} />
                                                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                                                                <Button variant="link" as="label" style={{ color: 'green', textDecoration: 'none', textTransform: 'capitalize', fontSize: '12px' }}>
                                                                    Replace Image
                                                                    <Form.Control type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handleImageChange} id="file-input" />
                                                                </Button>
                                                                <span style={{ borderLeft: '1px solid #ccc', height: '40px', margin: '0 10px' }}></span>
                                                                <Button variant="link" style={{ color: 'red', textDecoration: 'none', fontSize: '12px' }} onClick={() => requestRemoveMedia('logo', 'imageUrl', selectedFile ? setSelectedFile : null)}>
                                                                    Delete Image
                                                                </Button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <Card.Text>
                                                            <Button
                                                                color="primary"
                                                                as="label"
                                                                className="d-flex align-items-center justify-content-center"
                                                                style={{ color: '#fff' }}
                                                            >
                                                                <MdAddAPhoto size={24} className="me-2" />
                                                                Add Image
                                                                <Form.Control
                                                                    type="file"
                                                                    hidden
                                                                    accept=".jpg,.jpeg,.png,.webp"
                                                                    onChange={handleImageChange}
                                                                    id="file-input"
                                                                />
                                                            </Button>
                                                        </Card.Text>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card.Text as="div" className="text-muted" style={{ fontSize: '12px' }}>
                                                <p style={{ marginBottom: "0" }}><strong>File requirements: </strong></p>
                                                <p style={{ fontStyle: "italic", fontSize: "11px" }}>
                                                    <FaInfoCircle style={{ marginRight: '5px' }} />
                                                    JPG, PNG, or WebP up to 2 MB. Exact size: 588x558 pixels. Any other size opens a crop step.
                                                </p>
                                                {/* The artwork template for this slot, when one is configured. */}
                                                <ArtworkTemplateLink slotKey="retailer_wholesaler.logo" variant="inline" />
                                                <p style={{ fontSize: '12px' }}>
                                                    <FaCameraRetro style={{ marginRight: '5px' }} />
                                                    Ensure your product is in focus and make all the ingredients visible.
                                                </p>
                                                <p style={{ fontSize: '12px' }}>
                                                    <FaSquareFull style={{ marginRight: '5px' }} />
                                                    Show the full product and place it at the center of the frame.
                                                </p>
                                                <p style={{ fontSize: '12px' }}>
                                                    <FaSpellCheck style={{ marginRight: '5px' }} />
                                                    Remove distractions and avoid using text.
                                                </p>
                                                <p style={{ fontSize: '12px' }}>
                                                    <FaStar style={{ marginRight: '5px' }} />
                                                    Use a clear background and find bright lighting.
                                                </p>
                                            </Card.Text>
                                        </Col>
                                    </Row>
                                </Form.Group>
                                <Form.Group className="mb-1 mt-2">
                                    <Form.Label>Web Shop Header Banner</Form.Label>
                                    <Row>
                                        <Col md={6}>
                                            <Card style={{ width: '100%' }}>
                                                <Card.Body className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '220px' }}>
                                                    {isProcessing && selectedBannerFile ? (
                                                        <Spinner animation="border" />
                                                    ) : (formData.shopBannerUrl || formData.bannerUrl) ? (
                                                        <>
                                                            <img src={formData.shopBannerUrl || formData.bannerUrl} alt="Shop banner" style={{ width: '100%', aspectRatio: '1440 / 175', objectFit: 'contain' }} />
                                                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                                                                <Button variant="link" as="label" style={{ color: 'green', textDecoration: 'none', textTransform: 'capitalize', fontSize: '12px' }}>
                                                                    Replace Banner
                                                                    <Form.Control type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handleBannerChange} id="banner-file-input" />
                                                                </Button>
                                                                <span style={{ borderLeft: '1px solid #ccc', height: '40px', margin: '0 10px' }}></span>
                                                                <Button variant="link" style={{ color: 'red', textDecoration: 'none', fontSize: '12px' }} onClick={() => requestRemoveMedia('shopBanner', 'shopBannerUrl', selectedBannerFile ? setSelectedBannerFile : null)}>
                                                                    Delete Banner
                                                                </Button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <Card.Text>
                                                            <Button
                                                                color="primary"
                                                                as="label"
                                                                className="d-flex align-items-center justify-content-center"
                                                                style={{ color: '#fff' }}
                                                            >
                                                                <MdAddAPhoto size={24} className="me-2" />
                                                                Add Banner
                                                                <Form.Control
                                                                    type="file"
                                                                    hidden
                                                                    accept=".jpg,.jpeg,.png,.webp"
                                                                    onChange={handleBannerChange}
                                                                    id="banner-file-input"
                                                                />
                                                            </Button>
                                                        </Card.Text>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card.Text as="div" className="text-muted" style={{ fontSize: '12px' }}>
                                                <p style={{ marginBottom: "0" }}><strong>Banner File Requirements: </strong></p>
                                                <p style={{ fontStyle: "italic", fontSize: "11px" }}>
                                                    <FaInfoCircle style={{ marginRight: '5px' }} />
                                                    JPG, PNG, or WebP up to 5 MB. Exact size: 1440x175 pixels. Used only across the top of the web shop. Any other size opens a crop step.
                                                </p>
                                                {/* The artwork template for this slot, when one is configured. */}
                                                <ArtworkTemplateLink slotKey="retailer_wholesaler.shopBanner" variant="inline" />
                                            </Card.Text>
                                        </Col>
                                    </Row>
                                </Form.Group>
                                <Form.Group className="mb-1 mt-2">
                                    <Form.Label>Storefront Card Image</Form.Label>
                                    <Row>
                                        <Col md={6}>
                                            <Card style={{ width: '100%' }}>
                                                <Card.Body className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '220px' }}>
                                                    {isProcessing && selectedCardFile ? (
                                                        <Spinner animation="border" />
                                                    ) : formData.cardImageUrl ? (
                                                        <>
                                                            <img src={formData.cardImageUrl} alt="Storefront card" style={{ width: '100%', aspectRatio: '1280 / 720', objectFit: 'contain' }} />
                                                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                                                                <Button variant="link" as="label" style={{ color: 'green', textDecoration: 'none', textTransform: 'capitalize', fontSize: '12px' }}>
                                                                    Replace Image
                                                                    <Form.Control type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handleCardChange} id="card-file-input" />
                                                                </Button>
                                                                <span style={{ borderLeft: '1px solid #ccc', height: '40px', margin: '0 10px' }}></span>
                                                                <Button variant="link" style={{ color: 'red', textDecoration: 'none', fontSize: '12px' }} onClick={() => requestRemoveMedia('cardImage', 'cardImageUrl', selectedCardFile ? setSelectedCardFile : null)}>
                                                                    Delete Image
                                                                </Button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <Card.Text>
                                                            <Button
                                                                color="primary"
                                                                as="label"
                                                                className="d-flex align-items-center justify-content-center"
                                                                style={{ color: '#fff' }}
                                                            >
                                                                <MdAddAPhoto size={24} className="me-2" />
                                                                Add Image
                                                                <Form.Control
                                                                    type="file"
                                                                    hidden
                                                                    accept=".jpg,.jpeg,.png,.webp"
                                                                    onChange={handleCardChange}
                                                                    id="card-file-input"
                                                                />
                                                            </Button>
                                                        </Card.Text>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card.Text as="div" className="text-muted" style={{ fontSize: '12px' }}>
                                                <p style={{ marginBottom: "0" }}><strong>Card Image Requirements: </strong></p>
                                                <p style={{ fontStyle: "italic", fontSize: "11px" }}>
                                                    <FaInfoCircle style={{ marginRight: '5px' }} />
                                                    JPG, PNG, or WebP up to 5 MB. Exact size: 1280x720 pixels. Shown on the store's card in search results and listings. Any other size opens a crop step.
                                                </p>
                                                {/* The artwork template for this slot, when one is configured. */}
                                                <ArtworkTemplateLink slotKey="retailer_wholesaler.cardImage" variant="inline" />
                                                <p style={{ fontSize: '12px' }}>
                                                    <FaCameraRetro style={{ marginRight: '5px' }} />
                                                    Use a landscape (16:9) photo that represents the store.
                                                </p>
                                                <p style={{ fontSize: '12px' }}>
                                                    <FaSpellCheck style={{ marginRight: '5px' }} />
                                                    Keep important content centered and avoid text near the edges.
                                                </p>
                                                <p style={{ fontSize: '12px' }}>
                                                    <FaStar style={{ marginRight: '5px' }} />
                                                    Use bright, sharp imagery for a professional card.
                                                </p>
                                            </Card.Text>
                                        </Col>
                                    </Row>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        id="name"
                                        placeholder="Enter name"
                                        value={formData.name}
                                        onChange={canEditName ? handleChange : undefined}
                                        readOnly={!canEditName}
                                        required
                                        maxLength={120}
                                    />
                                    <Form.Text className="text-muted">
                                        {canEditName
                                            ? 'Changing this updates the public shop name.'
                                            : 'Only a super administrator can change the public shop name.'}
                                    </Form.Text>
                                </Form.Group>
                                <VendorTaglineControl
                                    value={formData.tagline}
                                    onChange={handleChange}
                                    groupClassName="mb-3"
                                />
                                <Form.Group className="mb-3">
                                    <Form.Label>Phone</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        id="phone"
                                        placeholder="Business phone number..."
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="email"
                                        id="email"
                                        placeholder="Email..."
                                        value={formData.email}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Location</Form.Label>
                                    {/* The same Places field as the other vendor forms:
                                        choosing a suggestion saves the address with its
                                        GPS point, the shop's pickup point for delivery
                                        fees. key remounts it when the market changes. */}
                                    <VendorAddressAutocomplete
                                        key={market.country}
                                        market={market}
                                        mapsApiLoaded={mapsApiLoaded}
                                        defaultValue={formData.address}
                                        onSelect={({ address, latitude, longitude }) =>
                                            setFormData((prev) => ({ ...prev, address, latitude, longitude }))
                                        }
                                        onClear={() => setFormData((prev) => ({ ...prev, address: '' }))}
                                    />
                                    <Form.Text muted>
                                        {hasPickupPoint
                                            ? `Pickup point: ${Number(formData.latitude).toFixed(6)}, ${Number(formData.longitude).toFixed(6)}`
                                            : 'No pickup point set yet. Start typing the address and choose a suggestion to set it.'}
                                    </Form.Text>
                                </Form.Group>
                                {/* Shop brief — both lines show on the retailer card in the
                                    apps and on the storefront intro, in the vendor's own words. */}
                                <Form.Group className="mb-3">
                                    <Form.Label>What you sell</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="typeOfGoods"
                                        id="typeOfGoods"
                                        maxLength={120}
                                        placeholder="e.g. Groceries, Household, Electronics"
                                        value={formData.typeOfGoods || ''}
                                        onChange={handleChange}
                                    />
                                    <Form.Text muted>Short, comma-separated. Shown as tags on your shop card.</Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>About your shop</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="about"
                                        id="about"
                                        maxLength={400}
                                        placeholder="A sentence or two customers see before they open your shop…"
                                        value={formData.about || ''}
                                        onChange={handleChange}
                                    />
                                    <Form.Text muted>{(formData.about || '').length}/400</Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.isActive}
                                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                                name="isActive"
                                                color="secondary"
                                            />
                                        }
                                        label={formData.isActive ? 'Active' : 'Deactivated'}
                                        classes={{ label: classes.label }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.promoted}
                                                onChange={(e) => setFormData({ ...formData, promoted: e.target.checked })}
                                                name="promoted"
                                                color="primary" // Different color for promoted switch
                                            />
                                        }
                                        label={formData.promoted ? 'Promoted' : 'Not Promoted'}
                                        classes={{ label: classes.label }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Created At</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="createdAt"
                                        id="createdAt"
                                        placeholder="Creation date..."
                                        value={new Date(formData.createdAt).toDateString()}
                                        readOnly
                                    />
                                </Form.Group>
                                <div className="d-flex justify-content-end">
                                    <Button
                                        variant="danger"
                                        onClick={onCancel}
                                        style={{ marginRight: '8px' }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={uploading}
                                        style={{ color: uploading ? 'gray' : 'white' }}
                                    >
                                        {uploading ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                    style={{ color: 'gray' }}
                                                />
                                                Saving...
                                            </>
                                        ) : (
                                            'Save'
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Image Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        {imagePreview && <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '400px' }} />}
                        {bannerPreview && <img src={bannerPreview} alt="Banner Preview" style={{ maxWidth: '100%', maxHeight: '400px' }} />}
                        {cardPreview && <img src={cardPreview} alt="Card Image Preview" style={{ maxWidth: '100%', maxHeight: '400px' }} />}
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        <hr />
                        <div className="text-muted" style={{ fontSize: '12px' }}>
                            <strong>The image must:</strong>
                            <ul style={{ fontSize: '12px' }}>
                                <li>Be JPG, PNG, or WebP format {isFormatCorrect ? <span className="text-success">✔</span> : <span className="text-danger">✘</span>}</li>
                                <li>Meet the role-specific file-size limit {isSizeCorrect ? <span className="text-success">✔</span> : <span className="text-danger">✘</span>}</li>
                                {imagePreview && (
                                    <li>Be exactly 588x558 pixels {isResolutionCorrect ? <span className="text-success">✔</span> : <span className="text-danger">✘</span>}</li>
                                )}
                                {bannerPreview && (
                                    <li>Be exactly 1440x175 pixels {isResolutionCorrect ? <span className="text-success">✔</span> : <span className="text-danger">✘</span>}</li>
                                )}
                                {cardPreview && (
                                    <li>Be exactly 1280x720 pixels {isResolutionCorrect ? <span className="text-success">✔</span> : <span className="text-danger">✘</span>}</li>
                                )}
                            </ul>
                        </div>
                    </div>
                    {uploadError && <p className="text-danger" style={{ fontSize: '12px' }}>{uploadError}</p>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={() => setShowPreviewModal(false)}>Cancel</Button>
                    {uploadError ? (
                        <Button variant="warning" onClick={() => document.getElementById('file-input').click()}>Try Another Image</Button>
                    ) : (
                        <Button variant="primary" onClick={() => setShowPreviewModal(false)}>Confirm Upload</Button>
                    )}
                </Modal.Footer>
            </Modal>
            {mediaCropDialog}
            <VendorProfileMediaDeleteDialog
                role={mediaDeleteRole}
                vendorType="retailer_wholesaler"
                vendorId={retailerWholesaler._id || retailerWholesaler.owner}
                vendorName={retailerWholesaler.name}
                media={formData}
                owner="the vendor"
                onClose={() => setMediaDeleteRole(null)}
                onRemoved={(cleared) => setFormData(prev => ({ ...prev, ...cleared }))}
            />
        </Container>
    );
};

export default EditRetailerWholesalerForm;
